import { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Stepper, Spinner } from 'components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IAnalysisForm, AnalysisInput, FormStep } from 'types';
import type { Swiper as SwiperType } from 'swiper';
import { Path } from 'utils';

import { FormStepCard } from './FormStepCard';
import {
  defaultValues,
  stepperData,
  stepCardData,
  handleNextButtonData,
} from './AnalysisForm.config';
import * as S from './AnalysisForm.styled';
import 'swiper/css';

export const AnalysisForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const navigate = useNavigate();
  const methods = useForm<IAnalysisForm>({ defaultValues });

  const steps = Object.keys(FormStep);
  const isLastStep = activeStep === steps.length;

  const handleNextStep = () => {
    swiperInstance?.slideNext();
    methods.resetField(AnalysisInput.INPUT);
    setActiveStep((prevActiveStep) =>
      prevActiveStep <= stepperData.length - 1 ? prevActiveStep + 1 : prevActiveStep
    );
  };

  const handlePrevStep = () => {
    swiperInstance?.slidePrev();
    setActiveStep((prevActiveStep) => (prevActiveStep > 0 ? prevActiveStep - 1 : prevActiveStep));
  };

  const currentInput: Array<string | File> = useWatch({
    control: methods.control,
    name: stepperData[activeStep]?.label,
  });

  const nextButtonData = handleNextButtonData(activeStep, currentInput.length, steps.length - 1);

  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return <FormStepCard activeStep={activeStep} {...stepCardData[FormStep.UPLOAD]} />;
      case 1:
        return <FormStepCard activeStep={activeStep} {...stepCardData[FormStep.VARIABLES]} />;
      case 2:
        return <FormStepCard activeStep={activeStep} {...stepCardData[FormStep.INDUSTRIES]} />;
      case 3:
        return <FormStepCard activeStep={activeStep} {...stepCardData[FormStep.COUNTRIES]} />;
      default:
        return <Spinner text="Analysis in progress" />;
    }
  };

  useEffect(() => {
    if (isLastStep) {
      setTimeout(() => {
        navigate(Path.ANALYSIS_FLOW);
      }, 2000);
    }
  }, [isLastStep]);

  return (
    <S.Container>
      <FormProvider {...methods}>
        <Stepper steps={stepperData} activeStep={activeStep} />
        <S.Content>
          <Swiper
            allowTouchMove={false}
            onSwiper={(swiper: SwiperType) => {
              setSwiperInstance(swiper);
              setActiveStep(swiper.activeIndex);
            }}
          >
            {steps.map((step) => (
              <SwiperSlide key={step}>{renderStep(activeStep)}</SwiperSlide>
            ))}
          </Swiper>
        </S.Content>
        <S.ButtonsWrapper>
          {!isLastStep && (
            <>
              {activeStep !== 0 && <S.PrevButton onClick={handlePrevStep}>prev step</S.PrevButton>}
              <S.NextButton onClick={handleNextStep} disabled={nextButtonData?.disabled}>
                {nextButtonData.label}
              </S.NextButton>
            </>
          )}
        </S.ButtonsWrapper>
      </FormProvider>
    </S.Container>
  );
};
